// Envelope = ZIP container of one or more files, prefixed with a 1-byte
// version marker that the .NET decrypt path strips before unzipping.
//
// Wire format inside the AES-GCM plaintext:  [0x01] || zip_bytes
// Constant mirrors GrataQ.Crypto.PayloadEnvelope.ZipContainerVersion.

import JSZip from 'jszip';

export const VERSION_BYTE = 0x01;

/**
 * Reserved filename inside the ZIP that carries the form-filled sender
 * info (R5). Mirrors GrataQ.Client.Core.SendService.SenderInfoFileName;
 * the WPF receive path strips it from the user-facing entry list.
 */
export const SENDER_INFO_FILENAME = '_grataq_sender_info.json';

/**
 * Build a ZIP archive from a FileList / array of File objects.
 * Returns a Uint8Array containing the raw ZIP bytes.
 *
 * Files are added under their original `name`. If two files share a name
 * the second overwrites the first (JSZip behaviour) — acceptable here
 * because the recipient sees the de-duplicated set anyway.
 *
 * <p>When <c>senderInfo</c> is non-null, a synthetic
 * <c>_grataq_sender_info.json</c> entry is added before the ZIP bytes
 * are generated. The recipient's WPF client parses it on receive and
 * surfaces the values in the Inbox row.</p>
 */
export async function buildZip(files, senderInfo = null) {
  const zip = new JSZip();
  for (const file of files) {
    zip.file(file.name, file);
  }
  if (senderInfo) {
    zip.file(SENDER_INFO_FILENAME, JSON.stringify(senderInfo));
  }
  return await zip.generateAsync({
    type: 'uint8array',
    // Default deflate compression. The data we ship is usually already
    // compressed (photos, PDFs), but uncompressed text would balloon the
    // upload otherwise.
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/** Prepend the 1-byte version marker. Returns a fresh Uint8Array. */
export function wrapWithVersion(zipBytes) {
  const out = new Uint8Array(zipBytes.length + 1);
  out[0] = VERSION_BYTE;
  out.set(zipBytes, 1);
  return out;
}
